import {
  DataGrid,
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
  GridFilterItem,
} from "@mui/x-data-grid";
import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { axiosInstance } from "../core/axios-interceptors";

export type GridCol = GridColDef;

interface ODataGridProps {
  url: string;
  columns: GridCol[];
  pageSize?: number;
  preFilter?: string; // OData filter string to be applied before user filters
  refreshKey?: number; // Key to force refresh when changed
}

export default function ODataGrid({
  url,
  columns,
  pageSize = 20,
  preFilter,
  refreshKey,
}: ODataGridProps) {
  const intl = useIntl();
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [page, setPage] = useState(0);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const [loading, setLoading] = useState(false);

  const [lastOdataUrl, setLastOdataUrl] = useState("pac");

  const escapeValue = (val: string) => val.replace(/'/g, "''");

  const buildODataFilter = (filters: GridFilterItem[]) => {
    return filters
      .map((f) => {
        const field = f.field;
        const col = columns.find((c) => c.field === field);
        const colType = col?.type || "string";
        const rawValue = f.value;

        if (!["isNotEmpty", "isEmpty"].includes(f.operator) && !rawValue) {
          return "";
        }

        const value =
          colType === "string" || colType === "singleSelect"
            ? `'${escapeValue(rawValue.toString())}'`
            : colType === "date"
              ? `${new Date(rawValue).toISOString()}`
              : rawValue;

        switch (f.operator) {
          case "=":
          case "equals":
          case "is":
            return `${field} eq ${value}`;
          case "!=":
          case "doesNotEqual":
            return `${field} ne ${value}`;
          case ">":
            return `${field} gt ${value}`;
          case "<":
            return `${field} lt ${value}`;
          case ">=":
            return `${field} ge ${value}`;
          case "<=":
            return `${field} le ${value}`;
          case "contains":
            return `contains(${field}, ${value})`;
          case "doesNotContain":
            return `not contains(${field}, ${value})`;
          case "startsWith":
            return `startswith(${field}, ${value})`;
          case "endsWith":
            return `endswith(${field}, ${value})`;
          case "isEmpty":
            return `${field} eq null`;
          case "isNotEmpty":
            return `${field} ne null`;
          case "isAnyOf":
            const values = Array.isArray(value) ? value : [value];
            return values
              .map((v: string) => `${field} eq ${`'${v.replace(/'/g, "''")}'`}`)
              .join(" or ");
          default:
            return "";
        }
      })
      .filter(Boolean)
      .join(" and ");
  };

  const fetchData = async () => {
    const skip = page * pageSize;
    const sort = sortModel[0];
    const orderBy = sort ? `$orderby=${sort.field} ${sort.sort}` : "";
    const userFilter = buildODataFilter(filterModel.items);

    // Combine preFilter with user filters
    const filterParts: string[] = [];
    if (preFilter) {
      filterParts.push(preFilter);
    }
    if (userFilter) {
      filterParts.push(userFilter);
    }
    const filter = filterParts.length > 0 ? filterParts.join(" and ") : "";
    const filterQuery = filter ? `$filter=${filter}` : "";
    const countQuery = `$count=true`;

    const queryParts = [
      `$top=${pageSize}`,
      `$skip=${skip}`,
      orderBy,
      filterQuery,
      countQuery,
    ]
      .filter(Boolean)
      .join("&");

    const finalUrl = `${url}?${queryParts}`;

    if (finalUrl === lastOdataUrl) {
      return;
    }
    setLastOdataUrl(finalUrl);

    setLoading(true);
    try {
      const response = await axiosInstance.get(finalUrl);

      const dateCols = internalColums.filter(
        (c) => c.type === "date" || c.type === "dateTime",
      );
      dateCols.forEach((c) =>
        (response.data.value as any[]).forEach(
          (r) => (r[c.field] = new Date(r[c.field])),
        ),
      );
      setRows(response.data.value || []);
      setRowCount(response.data["@odata.count"] || 0);
    } catch (err) {
      console.error("Errore fetch OData:", err);
      setRows([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Reset lastOdataUrl when refreshKey changes to force refresh
    if (refreshKey !== undefined) {
      setLastOdataUrl("");
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const mapColums = (): GridColDef[] => {
    const result: GridColDef[] = columns.map((c: GridCol) => {
      const shouldTranslateHeader = !c.field.startsWith("__") && !c.renderCell;
      return {
        ...c,
        field: c.field,
        headerName: c.headerName
          ? intl.formatMessage({ id: c.headerName })
          : (c.headerName ??
            (shouldTranslateHeader ? intl.formatMessage({ id: c.field }) : "")),
        width: c.width ?? 160,
      };
    });
    return result;
  };

  const internalColums = useMemo(mapColums, [columns, intl]);

  return (
    <DataGrid
      sx={{
        height: "100%",
      }}
      rows={rows}
      columns={internalColums}
      paginationModel={{ page, pageSize }}
      pagination
      paginationMode="server"
      onPaginationModelChange={(model: GridPaginationModel) =>
        setPage(model.page)
      }
      rowCount={rowCount}
      loading={loading}
      sortingMode="server"
      sortModel={sortModel}
      onSortModelChange={setSortModel}
      filterMode="server"
      filterModel={filterModel}
      onFilterModelChange={setFilterModel}
      getRowId={(row) => row.id || JSON.stringify(row)}
    />
  );
}
